/**
 * AgentDraftDock — the texting agent's pending reply, docked at the foot of the
 * lead's conversation, directly above the composer.
 *
 * WHY IT LIVES HERE. Copilot mode holds every agent reply for a human, and
 * until now the only place to release one was the console. So the recruiter
 * read the lead's message on the lead page, then left it to go approve — and in
 * practice many did not come back, they just typed a manual reply, which pauses
 * the agent and strands the draft it had already written. Putting the draft at
 * the bottom of the thread means the decision happens where the context is: you
 * read the lead's last text and the agent's proposed answer in one glance, and
 * Approve is closer to hand than the composer is.
 *
 * WHAT "WRITING" MEANS. The pulse is driven by `drafting_since`, stamped by
 * crmAgentTurn when it calls the model and cleared when the turn persists. It
 * is honest about being an activity signal, not a token stream: the agent emits
 * a JSON object, and every draft must clear the guardrail wall before a human
 * is allowed to see it, so there is no intermediate prose to show. The payoff is
 * the one that matters at the keyboard — "don't start typing, an answer lands in
 * a moment."
 *
 * The dock sits inside the fixed-height conversation card, so it is
 * flex-shrink-0 with a capped, self-scrolling body: however many drafts pile up,
 * the thread above keeps most of the screen.
 */
import { useState } from 'react';
import { Bot, Clock, AlertCircle, X } from 'lucide-react';
import api from '../../../services/api';
import { useNow } from '../../useNow';
import DraftCard from './DraftCard';
import { elapsedShort, timeShort, type DraftRow } from './shared';

/** An approved reply that has not gone out yet, and the reason it is waiting. */
export interface AgentInFlight {
  id: string;
  body: string;
  /** §6.7b human-first hold: the agent sends itself if nobody steps in. */
  holdUntil?: string | null;
  /** Quiet-hours deferral: it sends in the morning window. */
  deferredUntil?: string | null;
}

interface Props {
  /** Pending turns (drafted + guardrail-suppressed), oldest first. */
  drafts: DraftRow[];
  /** ISO instant the agent began writing, already TTL-expired by the server. */
  draftingSince: string | null;
  inFlight: AgentInFlight[];
  /** Re-fetch the agent card so an approved draft leaves the dock. */
  onChanged: () => void;
  /** Rendered inside the D-3 AttentionStrip, which owns the chrome and the
   *  height cap: drop this dock's own border/tint and its `max-h-[55%]` (that
   *  cap is measured against the conversation card, and inside the strip it
   *  would be a second, wrong ceiling). Absent = the standalone dock, byte for
   *  byte what the lead page renders today. */
  frameless?: boolean;
}

/** Three dots that actually move. The project has no `tailwindcss-animate`, so
 *  `animate-in`/`slide-in-*` are silent no-ops — core `animate-bounce` plus a
 *  staggered inline delay is the version that renders. */
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" aria-hidden="true">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          style={{ animationDelay: `${delay}ms` }}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 dark:bg-primary-400"
        />
      ))}
    </span>
  );
}

export default function AgentDraftDock({ drafts, draftingSince, inFlight, onChanged, frameless = false }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A per-second clock, mounted ONLY while something is actually pending — the
  // writing pulse counts in seconds (a model call is over before ago() leaves
  // 'now'), and an idle lead page should not tick.
  const live = Boolean(draftingSince) || drafts.length > 0 || inFlight.length > 0;
  const nowTs = useNow(1000, live);

  if (!live) return null;

  const failMsg = (err: any, verb: string) =>
    `${verb} failed: ${err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'request failed'}`;

  const approve = async (t: DraftRow, text?: string) => {
    setBusyId(t.id);
    setError(null);
    try {
      await api.post(`/api/crm/agent/turns/${t.id}/approve`, text ? { text } : {});
    } catch (err: any) {
      console.error('[AgentDraftDock] approve failed:', err);
      // §6.3b: the freshness gate refused — the lead texted past this draft.
      setError(err?.response?.data?.error === 'stale_inbound'
        ? 'Not sent — the lead texted again after this draft was written. The agent is writing a fresh reply that takes their newest message into account.'
        : failMsg(err, 'Approve'));
    } finally {
      setBusyId(null);
      // Reconcile either way: a lost race means this card is stale.
      onChanged();
    }
  };

  const reject = async (t: DraftRow, reason: string) => {
    setBusyId(t.id);
    setError(null);
    try {
      await api.post(`/api/crm/agent/turns/${t.id}/reject`, { reason: reason.trim() || null });
    } catch (err) {
      console.error('[AgentDraftDock] reject failed:', err);
      setError(failMsg(err, 'Reject'));
    } finally {
      setBusyId(null);
      onChanged();
    }
  };

  return (
    <div
      className={frameless
        ? 'flex flex-shrink-0 flex-col overflow-hidden'
        : 'flex max-h-[55%] flex-shrink-0 flex-col overflow-hidden border-t border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-gray-900/30'}
    >
      {error && (
        <div
          role="alert"
          className="flex flex-shrink-0 items-start gap-1.5 border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400"
        >
          <AlertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
            className="flex-shrink-0 rounded text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* The writing pulse. Deliberately ABOVE the drafts: while it shows, the
          thing below it is last turn's business, not the answer being written. */}
      {draftingSince && (
        <div
          role="status"
          aria-live="polite"
          className="flex flex-shrink-0 items-center gap-2 border-b border-primary-100 bg-primary-50/70 px-4 py-2 dark:border-primary-900/40 dark:bg-primary-900/15"
        >
          <Bot className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <span className="text-xs font-medium text-primary-800 dark:text-primary-300">Agent is writing a reply</span>
          <TypingDots />
          <span className="ml-auto whitespace-nowrap text-[11px] tabular-nums text-primary-600/70 dark:text-primary-400/70">
            {elapsedShort(draftingSince, nowTs)}
          </span>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* Approved but not yet gone: say why the thread looks idle instead of
            letting it read as a dropped reply. */}
        {inFlight.map((f) => {
          const label = f.holdUntil && new Date(f.holdUntil).getTime() > nowTs
            ? `held for a human until ${timeShort(f.holdUntil)} — it sends itself if nobody steps in`
            : f.deferredUntil && new Date(f.deferredUntil).getTime() > nowTs
              ? `held for the morning window (quiet hours) — sends ${timeShort(f.deferredUntil)}`
              : 'sending shortly (natural typing delay)';
          return (
            <p key={f.id} className="flex items-start gap-1.5 px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-500" aria-hidden="true" />
              <span className="min-w-0">
                <span className="font-medium text-gray-700 dark:text-gray-300">Approved reply {label}:</span>{' '}
                <span className="line-clamp-2">{f.body}</span>
              </span>
            </p>
          );
        })}

        {drafts.length > 0 && (
          <div className="flex flex-col gap-2 p-2.5">
            {drafts.map((d) => (
              <DraftCard
                key={d.id}
                t={d}
                busy={busyId === d.id}
                nowTs={nowTs}
                compact
                heading="The agent wants to send this"
                onApprove={approve}
                onReject={reject}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
